/**
 * @typedef {{ providerId?: string | null } | null} ProviderLike
 * @typedef {{ emailVerified?: boolean, providerData?: ProviderLike[] | null }} AuthUserLike
 */

/**
 * @param {AuthUserLike | null | undefined} user
 */
function userHasPasswordProvider(user) {
  return (user?.providerData ?? []).some(
    (provider) => provider?.providerId === "password",
  );
}

/**
 * Password signups need a confirm-email link. Google/Facebook usually already are.
 * @param {AuthUserLike | null | undefined} user
 */
function shouldQueueEmailVerification(user) {
  if (!user || user.emailVerified) {
    return false;
  }
  return userHasPasswordProvider(user);
}

/**
 * Welcome mail waits until the address is confirmed. Google/Facebook sign-ins
 * are usually already verified, so they still get welcome on first sync.
 * @param {AuthUserLike | null | undefined} user
 */
function shouldQueueWelcomeEmail(user) {
  return Boolean(user && user.emailVerified);
}

/**
 * Whether this account is restricted pending email confirmation.
 *
 * Browsing stays open; the restriction applies to actions that move money or
 * create public obligations (booking, hosting, publishing, subscribing), which
 * `api/_lib/require-verified-email.js` enforces server-side. Federated sign-ins
 * arrive verified, so in practice this is password signups that never confirmed.
 *
 * @param {AuthUserLike | null | undefined} user
 */
function isRestrictedForUnverifiedEmail(user) {
  if (!user || user.emailVerified) {
    return false;
  }
  return userHasPasswordProvider(user);
}

module.exports = {
  userHasPasswordProvider,
  shouldQueueEmailVerification,
  shouldQueueWelcomeEmail,
  isRestrictedForUnverifiedEmail,
};
